// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { ERROR_CODE_MAPPING, ERROR_MESSAGES } from '@/api/constants';
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { SupportAlert } from '@/components/support-alert';
import { CloseRounded } from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { classifyTriageCustomer } from '@/customer/api/triage-classify-customer';
import { CustomerTriage } from '@/customer/api/triage-types';
import { CreateCustomerFormFields } from '@/customer/components/create-customer-form-fields';
import {
  CreateCustomerFormData,
  DEFAULT_BILLING_VALUES,
  createCustomerFormResolver,
} from '@/customer/components/create-customer-form-schema';
import { CustomerSearch } from '@/customer/components/customer-search';
import { TriageInfoRow } from '@/customer/components/triage-info-row';
import { customerTriageQueryKey } from '@/customer/triage-query-keys';
import { SparseCustomer } from '@/sparse/api/types';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link as MuiLink,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

interface CustomerTriageClassifyDialogProps {
  isOpen: boolean;
  triage: CustomerTriage | null;
  onClose: () => void;
}

function buildContactName(
  firstName: string | null,
  lastName: string | null
): string | null {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : null;
}

function buildDefaultValues(triage: CustomerTriage): CreateCustomerFormData {
  return {
    name: triage.companyName || triage.displayName,
    domain: triage.domain || null,
    isCustomer: true,
    isSupplier: false,
    billingAddress: triage.billingAddress || null,
    billingContact: {
      name:
        buildContactName(
          triage.billingContact?.firstName ?? null,
          triage.billingContact?.lastName ?? null
        ) || null,
      email: triage.billingContact?.email || null,
      phone: triage.billingContact?.phone || null,
    },
  };
}

export function CustomerTriageClassifyDialog(
  props: CustomerTriageClassifyDialogProps
) {
  const { isOpen, triage, onClose } = props;
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [matchedCustomer, setMatchedCustomer] = useState<SparseCustomer | null>(
    null
  );
  const [showCreateForm, setShowCreateForm] = useState(false);
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const defaultFormValues = triage
    ? buildDefaultValues(triage)
    : {
        name: '',
        domain: null,
        isCustomer: true,
        isSupplier: false,
        ...DEFAULT_BILLING_VALUES,
      };

  const {
    control,
    reset: resetForm,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors },
  } = useForm<CreateCustomerFormData>({
    resolver: createCustomerFormResolver,
    defaultValues: defaultFormValues,
  });

  useEffect(() => {
    if (isOpen && triage) {
      resetForm(buildDefaultValues(triage));
      setMatchedCustomer(null);
      setShowCreateForm(false);
      setErrorMessage('');
    }
  }, [isOpen, triage, resetForm]);

  const isMatchMode = matchedCustomer !== null;

  const classifyMutation = useMutation({
    mutationFn: async (formData?: CreateCustomerFormData) => {
      if (!triage) {
        throw new Error('No triage entry selected');
      }

      if (matchedCustomer) {
        return classifyTriageCustomer(triage.key, {
          customerId: matchedCustomer.id,
        });
      }

      if (!formData) {
        throw new Error('Form data is required when creating a new company');
      }

      return classifyTriageCustomer(triage.key, {
        newCustomer: {
          name: formData.name.trim(),
          isCustomer: formData.isCustomer,
          isSupplier: formData.isSupplier,
          domain: formData.domain?.trim() || null,
          billingAddress: formData.billingAddress?.trim() || null,
          billingContact: {
            name: formData.billingContact.name?.trim() || null,
            email: formData.billingContact.email?.trim() || null,
            phone: formData.billingContact.phone?.trim() || null,
          },
        },
      });
    },
    onSuccess: (updatedTriage) => {
      queryClient.invalidateQueries({ queryKey: customerTriageQueryKey });
      queryClient.invalidateQueries({ queryKey: ['customer'] });
      setErrorMessage('');
      setMatchedCustomer(null);
      setShowCreateForm(false);
      onClose();

      if (updatedTriage.customer) {
        showToast({
          severity: 'success',
          duration: 5000,
          element: (
            <span>
              View{' '}
              <Link
                to="/customers/$customerKey"
                params={{ customerKey: updatedTriage.customer.key }}
              >
                <MuiLink
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                    cursor: 'pointer',
                  }}
                >
                  {updatedTriage.customer.name}
                </MuiLink>
              </Link>
            </span>
          ),
        });
      }
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      if (
        apiError.apiStatusCode ===
        ERROR_CODE_MAPPING.customer_invalid_billing_contact_email
      ) {
        setError('billingContact.email', {
          type: 'custom',
          message: ERROR_MESSAGES.customer_invalid_billing_contact_email,
        });
        return;
      }

      const curated = getUserFacingErrorMessage(apiError);
      if (
        curated === SUPPORT_FALLBACK_ERROR_MESSAGE &&
        !apiError.sentToSentry
      ) {
        captureException(error);
      }
      setErrorMessage(curated);
    },
  });

  const handleConfirmMatch = () => {
    setErrorMessage('');
    classifyMutation.mutate(undefined);
  };

  const handleConfirmCreate = (data: CreateCustomerFormData) => {
    setErrorMessage('');
    classifyMutation.mutate(data);
  };

  const handleClose = () => {
    if (classifyMutation.isPending) {
      return;
    }
    setErrorMessage('');
    setMatchedCustomer(null);
    setShowCreateForm(false);
    resetForm(defaultFormValues);
    onClose();
  };

  const handleDialogClose = (_event: object, reason: string) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      if (classifyMutation.isPending) {
        return;
      }
    }
    handleClose();
  };

  const handleCreateFromSearch = (name: string) => {
    setValue('name', name);
    setShowCreateForm(true);
    setMatchedCustomer(null);
  };

  const handleBack = () => {
    setShowCreateForm(false);
    if (triage) {
      resetForm(buildDefaultValues(triage));
    }
  };

  if (!triage) {
    return null;
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="classify-customer-triage-dialog-title"
    >
      <DialogTitle
        id="classify-customer-triage-dialog-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          pb: 0,
        }}
      >
        <Box>
          <Typography variant="h6" component="span">
            Classify customer
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Match this QBO customer to an existing company or create a new one
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleClose}
          disabled={classifyMutation.isPending}
          aria-label="Close"
        >
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 2 }}>
          {/* Triage info card */}
          {!showCreateForm && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <TriageInfoRow label="Display name" value={triage.displayName} />
              <TriageInfoRow label="Company" value={triage.companyName} />
              <TriageInfoRow label="Domain" value={triage.domain} />
              <TriageInfoRow label="Email" value={triage.email} />
            </Box>
          )}

          {/* Billing contact info */}
          {triage.billingContact && !showCreateForm && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Billing contact
              </Typography>
              <TriageInfoRow
                label="Name"
                value={buildContactName(
                  triage.billingContact.firstName,
                  triage.billingContact.lastName
                )}
              />
              <TriageInfoRow
                label="Email"
                value={triage.billingContact.email}
              />
              <TriageInfoRow
                label="Phone"
                value={triage.billingContact.phone}
              />
              <TriageInfoRow
                label="Address"
                value={triage.billingContact.billingAddress}
              />
            </Box>
          )}

          {/* Match to existing customer */}
          {!showCreateForm && (
            <>
              <Box>
                <Typography variant="subtitle1" gutterBottom mb={1}>
                  Match to existing company
                </Typography>
                <CustomerSearch
                  value={matchedCustomer}
                  onChange={(value) => {
                    setMatchedCustomer(value);
                  }}
                  onCreate={handleCreateFromSearch}
                />
              </Box>

              <Divider />

              <Button
                variant="outlined"
                onClick={() => {
                  setShowCreateForm(true);
                  setMatchedCustomer(null);
                }}
                disabled={classifyMutation.isPending}
              >
                Create new company
              </Button>
            </>
          )}

          {/* Create new customer form */}
          {showCreateForm && (
            <Stack spacing={2}>
              <Typography variant="subtitle1">Create new company</Typography>
              <CreateCustomerFormFields
                mode="full"
                control={control}
                watch={watch}
                setValue={setValue}
                errors={errors}
              />
            </Stack>
          )}

          {errorMessage &&
            (errorMessage === SUPPORT_FALLBACK_ERROR_MESSAGE ? (
              <SupportAlert
                message={errorMessage}
                onClose={() => setErrorMessage('')}
              />
            ) : (
              <Alert severity="error" onClose={() => setErrorMessage('')}>
                {errorMessage}
              </Alert>
            ))}
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {showCreateForm && (
            <Button
              variant="text"
              onClick={handleBack}
              disabled={classifyMutation.isPending}
            >
              Back
            </Button>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="text"
            onClick={handleClose}
            disabled={classifyMutation.isPending}
          >
            Cancel
          </Button>
          {showCreateForm ? (
            <Button
              variant="contained"
              onClick={handleSubmit(handleConfirmCreate)}
              disabled={classifyMutation.isPending}
            >
              {classifyMutation.isPending
                ? 'Creating...'
                : 'Create and classify'}
            </Button>
          ) : (
            <Tooltip
              title={
                !isMatchMode
                  ? 'Select an existing company or create a new one'
                  : ''
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  onClick={handleConfirmMatch}
                  disabled={classifyMutation.isPending || !isMatchMode}
                >
                  {classifyMutation.isPending
                    ? 'Linking...'
                    : 'Match to existing company'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
